import React from 'react';
import styled from 'styled-components';
import { Container, Text } from '../styles/components';
import SectionNumber from './SectionNumber';

const SectionWrapper = styled.section`
  min-height: calc(100svh - 100px);
  padding: 100px 0 ${props => props.theme.spacing.xxl} 0;
  position: relative;
  background: ${props => props.theme.colors.bone};
  display: flex;
  align-items: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/assets/patterns/Contours - clipped (light).svg');
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    opacity: 1;
    z-index: 1;
  }
`;

const ContentContainer = styled(Container)`
  max-width: ${props => props.theme.spacing.maxWidth};
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const HeaderWrapper = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxl};
`;

const SectionHeading = styled.h2`
  font-family: ${props => props.theme.typography.heading.fontFamily};
  font-weight: ${props => props.theme.typography.heading.fontWeight};
  font-size: 2.5rem;
  line-height: 1.1;
  color: ${props => props.theme.colors.charcoal};
  margin: 0 0 ${props => props.theme.spacing.md} 0;

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    font-size: 2rem; /* Reduced from 2.5rem */
  }
`;

const Rule = styled.hr`
  width: 180px;
  height: 0.75px;
  background-color: ${props => props.theme.colors.charcoal};
  border: none;
  margin: 0 auto;
`;

const ThreeColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.lg};
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ColumnImage = styled.img`
  width: 90%;
  height: auto;
  object-fit: contain;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const BodyText = styled(Text)`
  line-height: 1.6;
  text-align: center;
  color: ${props => props.theme.colors.charcoal};
  margin-bottom: ${props => props.theme.spacing.md};
  font-weight: 400;

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    font-size: 1.125rem !important; /* Reduced from 1.5rem (large) */
  }
`;

const Highlight = styled.p`
  font-family: ${props => props.theme.typography.highlight.fontFamily};
  font-weight: ${props => props.theme.typography.highlight.fontWeight};
  font-style: ${props => props.theme.typography.highlight.fontStyle};
  font-size: ${props => props.theme.typography.sizes.bodyLarge};
  line-height: 1.3;
  color: ${props => props.theme.colors.terracotta};
  text-align: center;
  text-align: center;
  margin: 0;

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    font-size: 1.125rem; /* Reduced from 1.5rem */
  }
`;

const ProblemSection = () => {
  return (
    <SectionWrapper id="problem">
      <ContentContainer>
        <HeaderWrapper>
          <SectionHeading>
            What's a weak pitch costing you?
          </SectionHeading>
          <Rule />
        </HeaderWrapper>

        <ThreeColumnGrid>
          <Column>
            <ColumnImage
              src="/assets/founder1.png"
              alt="Founders spend too much time fundraising"
            />
            <BodyText size="large">
              Founders spend 15-25 hrs/week fundraising. That's 3-4 months of full-time work for a typical round.
            </BodyText>
            <Highlight>
              Let's halve this
            </Highlight>
          </Column>

          <Column>
            <ColumnImage
              src="/assets/founder2.png"
              alt="Runway time spent raising"
            />
            <BodyText size="large">
              Up to 50% of runway time is spent raising the next round - an average of 9 months raising instead of building.
            </BodyText>
            <Highlight>
              Let's halve this too
            </Highlight>
          </Column>

          <Column>
            <ColumnImage
              src="/assets/founder3.png"
              alt="Stop going bonkers on your pitch"
            />
            <BodyText size="large">
              You're on deck v48.5 FINAL and you're going bonkers?
            </BodyText>
            <Highlight>
              Let's stop this altogether
            </Highlight>
          </Column>
        </ThreeColumnGrid>
      </ContentContainer>

      <SectionNumber number={2} light />
    </SectionWrapper>
  );
};

export default ProblemSection;
